import * as React from 'react';
import {
  ApiError,
  getGetAccessContextQueryKey,
  getListAccessContextsQueryKey,
  isEntityIndependentRequest,
  setApiEntityScope,
  useGetAccessContext,
  useListAccessContexts,
  type AccessIdentitySummary,
  type AccessContextOption,
  type AccessContextSnapshot
} from '@cdf/api-client-react';
import { useQueryClient, type Query } from '@tanstack/react-query';

import { useSession } from '@/components/auth/user-provider';

type AccessStatus = 'idle' | 'loading' | 'switching' | 'ready' | 'noAccess' | 'error';

interface TenantEntityContextValue {
  status: AccessStatus;
  contexts: AccessContextOption[];
  selected: AccessContextOption | null;
  snapshot: AccessContextSnapshot | null;
  error: string | null;
  contextOffset: number;
  contextTotal: number;
  contextsLoading: boolean;
  setContextOffset: (offset: number) => void;
  hasPermission: (permission: string) => boolean;
  hasPlatformPermission: (permission: string) => boolean;
  enabledModules: string[];
  hasModule: (moduleKey: string) => boolean;
  isPlatformAdmin: boolean;
  platformPermissions: string[];
  accessSummary: AccessIdentitySummary | null;
  platformRoles: AccessIdentitySummary['platformRoles'];
  tenants: AccessIdentitySummary['tenants'];
  eligibleRoles: AccessIdentitySummary['eligibleRoles'];
  activeElevations: AccessIdentitySummary['activeElevations'];
  activeSupportSessions: AccessIdentitySummary['activeSupportSessions'];
  selectContext: (tenantId: string, entityId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const TenantEntityContext = React.createContext<TenantEntityContextValue | null>(null);

function isScopedQuery(query: Query): boolean {
  return typeof query.queryKey[0] !== 'string' || !isEntityIndependentRequest(query.queryKey[0]);
}

function contextKey(context: Pick<AccessContextOption, 'tenantId' | 'entityId'>): string {
  return `${context.tenantId}:${context.entityId}`;
}

function storedContextKey(identity: string): string {
  return `cdf-active-entity:${identity.toLowerCase()}`;
}

function readStoredContext(identity: string): string | null {
  try {
    return localStorage.getItem(storedContextKey(identity));
  } catch {
    return null;
  }
}

function writeStoredContext(identity: string, context: AccessContextOption): void {
  try {
    localStorage.setItem(storedContextKey(identity), contextKey(context));
  } catch {
    // The active session still keeps its selected context when storage is unavailable.
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return 'You no longer have permission to use this entity.';
  }
  return error instanceof Error ? error.message : 'Could not load your entity access.';
}

export function TenantEntityProvider({ children }: { children: React.ReactNode }) {
  const { session } = useSession();
  const identity = session.status === 'signedIn' ? session.user.email || session.user.name : session.status;
  return <TenantEntitySession key={identity} children={children} />;
}

function TenantEntitySession({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const internalUser = session.status === 'signedIn' && session.user.role !== 'customer';
  const identity = session.status === 'signedIn'
    ? session.user.email || session.user.name
    : '';
  const [contexts, setContexts] = React.useState<AccessContextOption[]>([]);
  const [contextOffset, setContextOffset] = React.useState(0);
  const [selected, setSelected] = React.useState<AccessContextOption | null>(null);
  const [switching, setSwitching] = React.useState(false);
  const [localError, setLocalError] = React.useState<string | null>(null);
  const selectionGeneration = React.useRef(0);

  const contextsQuery = useListAccessContexts(
    { limit: 100, offset: contextOffset },
    {
      query: {
        queryKey: [...getListAccessContextsQueryKey({ limit: 100, offset: contextOffset }), identity],
        enabled: internalUser,
        retry: 1,
        staleTime: 0,
        refetchInterval: 30_000
      }
    }
  );

  // Keep identity-wide temporary access visible while paging tenant/entity choices.
  const identityQuery = useListAccessContexts({ limit: 100, offset: 0 }, {
    query: {
      queryKey: [...getListAccessContextsQueryKey({ limit: 100, offset: 0 }), identity],
      enabled: internalUser,
      retry: 1,
      staleTime: 0,
      refetchInterval: 30_000
    }
  });
  const refetchIdentity = identityQuery.refetch;

  const contextQuery = useGetAccessContext({
    query: {
      queryKey: [
        ...getGetAccessContextQueryKey(),
        selected?.tenantId ?? 'unselected',
        selected?.entityId ?? 'unselected'
      ],
      enabled: internalUser && selected !== null,
      retry: false,
      staleTime: 0,
      refetchInterval: 30_000
    }
  });
  const refetchContexts = contextsQuery.refetch;
  const refetchSelectedContext = contextQuery.refetch;

  const clearScope = React.useCallback(async () => {
    selectionGeneration.current += 1;
    setSwitching(true);
    setSelected(null);
    setApiEntityScope(null);
    await queryClient.cancelQueries({ predicate: isScopedQuery });
    queryClient.removeQueries({ predicate: isScopedQuery });
  }, [queryClient]);

  const reloadAuthorizedContexts = React.useCallback(async () => {
    await clearScope();
    const result = await refetchContexts();
    const allowed = result?.data?.items ?? [];
    setContexts(allowed);
    setSwitching(false);
  }, [clearScope, refetchContexts]);

  const activateContext = React.useCallback(async (next: AccessContextOption) => {
    const generation = selectionGeneration.current + 1;
    selectionGeneration.current = generation;
    setLocalError(null);
    setSwitching(true);
    setSelected(null);
    setApiEntityScope(null);
    await queryClient.cancelQueries({ predicate: isScopedQuery });
    queryClient.removeQueries({ predicate: isScopedQuery });
    if (selectionGeneration.current !== generation) return;
    setApiEntityScope({ tenantId: next.tenantId, entityId: next.entityId });
    setSelected(next);
    writeStoredContext(identity, next);
  }, [identity, queryClient]);

  const selectContext = React.useCallback(async (tenantId: string, entityId: string) => {
    const tenant = contextsQuery.data?.tenants?.find((item) => item.id === tenantId && item.status === 'active');
    const entity = tenant?.entities.find((item) => item.id === entityId && item.status === 'active');
    let next = [selected, ...contexts].find(
      (candidate) => candidate?.tenantId === tenantId && candidate.entityId === entityId
    ) ?? (tenant && entity ? { tenantId: tenant.id, tenantName: tenant.name, entityId: entity.id, entityName: entity.name } : undefined);
    if (!next) {
      // Invitation redemption can make a context available after this render. Resolve
      // against the completed refresh result so the first click lands in that tenant.
      const refreshed = await refetchContexts();
      const fresh = refreshed.data;
      const freshTenant = fresh?.tenants?.find((item) => item.id === tenantId && item.status === 'active');
      const freshEntity = freshTenant?.entities.find((item) => item.id === entityId && item.status === 'active');
      next = fresh?.items.find((candidate) => candidate.tenantId === tenantId && candidate.entityId === entityId)
        ?? (freshTenant && freshEntity ? {
          tenantId: freshTenant.id,
          tenantName: freshTenant.name,
          entityId: freshEntity.id,
          entityName: freshEntity.name
        } : undefined);
      if (fresh) setContexts(fresh.items);
    }
    if (!next) {
      throw new Error('That entity is not available to this account.');
    }
    if (selected && contextKey(selected) === contextKey(next)) return;
    await activateContext(next);
  }, [activateContext, contexts, contextsQuery.data, refetchContexts, selected]);

  const refresh = React.useCallback(async () => {
    setLocalError(null);
    await Promise.all([
      refetchContexts(),
      contextOffset > 0 ? refetchIdentity() : Promise.resolve(),
      selected ? refetchSelectedContext() : Promise.resolve()
    ]);
  }, [contextOffset, refetchContexts, refetchIdentity, refetchSelectedContext, selected]);

  React.useEffect(() => {
    if (!internalUser) {
      setContexts([]);
      setSelected(null);
      setSwitching(false);
      setLocalError(null);
      setApiEntityScope(null);
      return;
    }
    if (!contextsQuery.data) return;

    const allowed = contextsQuery.data.items;
    setContexts(allowed);
    if (selected) {
      const current = allowed.find((candidate) => contextKey(candidate) === contextKey(selected));
      if (current) {
        if (current.tenantName !== selected.tenantName || current.entityName !== selected.entityName) {
          setSelected(current);
        }
        return;
      }
      // The selected grant is revalidated independently; it may be on another page.
      if (contextOffset > 0 || contextsQuery.data.total > 0) return;
    }
    if (switching) return;
    if (allowed.length === 0) {
      if (selected) void clearScope().then(() => setSwitching(false));
      return;
    }

    const stored = readStoredContext(identity);
    const next = allowed.find((candidate) => contextKey(candidate) === stored) ?? allowed[0];
    if (next) void activateContext(next);
  }, [activateContext, clearScope, contextOffset, contextsQuery.data, identity, internalUser, selected, switching]);

  React.useEffect(() => {
    if (!selected || !contextQuery.data) return;
    if (
      contextQuery.data.tenantId !== selected.tenantId ||
      contextQuery.data.entityId !== selected.entityId
    ) {
      void reloadAuthorizedContexts();
      return;
    }
    setSwitching(false);
  }, [contextQuery.data, reloadAuthorizedContexts, selected]);

  React.useEffect(() => {
    if (!contextQuery.error) return;
    if (contextQuery.error instanceof ApiError && contextQuery.error.code === 'SCOPE_NOT_FOUND') {
      void reloadAuthorizedContexts();
      return;
    }
    setSwitching(false);
    setLocalError(errorMessage(contextQuery.error));
  }, [contextQuery.error, reloadAuthorizedContexts]);

  React.useEffect(() => {
    const onAccessInvalidated = () => {
      void reloadAuthorizedContexts();
    };
    window.addEventListener('cdf:access-invalidated', onAccessInvalidated);
    return () => window.removeEventListener('cdf:access-invalidated', onAccessInvalidated);
  }, [reloadAuthorizedContexts]);

  React.useEffect(() => {
    const expiries = [...(identityQuery.data?.activeElevations ?? []), ...(identityQuery.data?.activeSupportSessions ?? [])]
      .map((item) => Date.parse(item.expiresAt)).filter((expiry) => expiry > Date.now());
    if (!expiries.length) return;
    const timer = window.setTimeout(() => { void reloadAuthorizedContexts(); if (contextOffset > 0) void refetchIdentity(); }, Math.min(2_147_483_647, Math.max(0, Math.min(...expiries) - Date.now()) + 10));
    return () => window.clearTimeout(timer);
  }, [contextOffset, identityQuery.data, refetchIdentity, reloadAuthorizedContexts]);

  React.useEffect(() => () => {
    selectionGeneration.current += 1;
    setApiEntityScope(null);
    void queryClient.cancelQueries({ predicate: isScopedQuery });
    queryClient.removeQueries({ predicate: isScopedQuery });
  }, [queryClient]);

  let status: AccessStatus = 'idle';
  if (internalUser) {
    if (localError || contextsQuery.isError) status = 'error';
    else if (contextsQuery.isPending && !selected) status = 'loading';
    else if (!contextsQuery.isPending && contexts.length === 0 && !selected) status = 'noAccess';
    else if (switching || !selected || contextQuery.isPending) status = 'switching';
    else if (contextQuery.data?.tenantId === selected.tenantId && contextQuery.data?.entityId === selected.entityId) status = 'ready';
    else status = 'loading';
  }

  const snapshot = status === 'ready' ? contextQuery.data ?? null : null;
  const permissions = React.useMemo(() => new Set(snapshot?.permissions ?? []), [snapshot]);
  const enabledModuleSet = React.useMemo(() => new Set(snapshot?.enabledModules ?? []), [snapshot]);
  const value = React.useMemo<TenantEntityContextValue>(() => ({
    status,
    contexts,
    selected,
    snapshot,
    error: localError ?? (contextsQuery.isError ? errorMessage(contextsQuery.error) : null),
    contextOffset,
    contextTotal: contextsQuery.data?.total ?? contexts.length,
    contextsLoading: contextsQuery.isFetching,
    setContextOffset,
    hasPermission: (permission) => permissions.has(permission),
    enabledModules: snapshot?.enabledModules ?? [],
    hasModule: (moduleKey) => enabledModuleSet.has(moduleKey),
    hasPlatformPermission: (permission) => internalUser && !contextsQuery.isError && (contextsQuery.data?.platformPermissions ?? []).includes(permission),
    isPlatformAdmin: internalUser && !contextsQuery.isError && (contextsQuery.data?.isPlatformAdmin ?? false),
    accessSummary: internalUser && !contextsQuery.isError ? contextsQuery.data ?? null : null,
    platformPermissions: internalUser && !contextsQuery.isError ? contextsQuery.data?.platformPermissions ?? [] : [],
    platformRoles: internalUser && !contextsQuery.isError ? contextsQuery.data?.platformRoles ?? [] : [],
    tenants: internalUser && !contextsQuery.isError ? contextsQuery.data?.tenants ?? [] : [],
    eligibleRoles: internalUser && !identityQuery.isError ? identityQuery.data?.eligibleRoles ?? [] : [],
    activeElevations: internalUser && !identityQuery.isError ? identityQuery.data?.activeElevations ?? [] : [],
    activeSupportSessions: internalUser && !identityQuery.isError ? identityQuery.data?.activeSupportSessions ?? [] : [],
    selectContext,
    refresh
  }), [
    status,
    contexts,
    selected,
    snapshot,
    localError,
    contextsQuery.isError,
    contextsQuery.error,
    contextOffset,
    contextsQuery.data,
    identityQuery.data,
    identityQuery.isError,
    internalUser,
    contextsQuery.isFetching,
    permissions,
    enabledModuleSet,
    selectContext,
    refresh
  ]);

  return <TenantEntityContext.Provider value={value}>{children}</TenantEntityContext.Provider>;
}

export function useTenantEntity(): TenantEntityContextValue {
  const value = React.useContext(TenantEntityContext);
  if (!value) throw new Error('useTenantEntity must be used inside TenantEntityProvider.');
  return value;
}
